import { useGetList, type Identifier } from "ra-core";
import { useMemo } from "react";

import type { Contact, SlateForm } from "../types";
import {
  simulateFormSubmissions,
  type FormActivity,
  type FormSubmission,
} from "./assetActivity";

/**
 * Real per-form fill activity from the event spine (round: forms actually
 * work now). A submission only appears here once the automation worker's
 * identity pass has matched or created its contact - the raw, contact-less
 * capture is the same event type (form.submitted) but never carries
 * contact_id, so it's excluded by construction, not by a separate filter.
 * The demo/FakeRest provider has no activity_events table, so it keeps using
 * the deterministic sample generator instead (see assetActivity.ts).
 */

const IS_DEMO = import.meta.env.VITE_IS_DEMO === "true";

interface TouchSnapshot {
  utmSource?: string;
  utmMedium?: string;
  referrer?: string;
}

interface FormSubmittedEventRow {
  id: string;
  contact_id: number | null;
  occurred_at: string;
  payload: {
    formId: string;
    pageUrl?: string;
    touch?: TouchSnapshot;
  };
}

/**
 * Rough source bucket from the submission's own attribution snapshot - no
 * inference beyond what the event already carries. A fuller channel model
 * needs touch.captured events linked to the contact too, which is a
 * separate, bigger gap (decision #44's identity stitching, forms-only so far).
 */
const classifyChannel = (touch?: TouchSnapshot): string => {
  if (!touch) return "direct";
  const medium = touch.utmMedium?.toLowerCase() ?? "";
  if (/cpc|ppc|paid/.test(medium)) return "paid";
  if (touch.utmSource) return "campaign";
  if (touch.referrer) return "referral";
  return "direct";
};

const nameFor = (contactId: number, contactsById: Map<Identifier, Contact>): string => {
  const c = contactsById.get(contactId);
  const name = c ? `${c.first_name ?? ""} ${c.last_name ?? ""}`.trim() : "";
  return name || `Contact #${contactId}`;
};

export const useFormActivity = (
  forms: SlateForm[],
  contacts: Contact[],
): Map<string, FormActivity> => {
  const { data: events = [] } = useGetList<FormSubmittedEventRow>(
    "activity_events",
    {
      filter: { type: "form.submitted" },
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "occurred_at", order: "DESC" },
    },
    {
      enabled: !IS_DEMO,
      onError: () => {
        // No activity_events resource in the demo/FakeRest provider - the
        // simulate() fallback below is what renders there instead.
      },
    },
  );

  return useMemo(() => {
    if (IS_DEMO) {
      return new Map(forms.map((f) => [f.id, simulateFormSubmissions(f, contacts)]));
    }

    const contactsById = new Map(contacts.map((c) => [c.id, c]));
    const cutoff = Date.now() - 30 * 86_400_000;
    const byForm = new Map<string, FormActivity>();

    for (const f of forms) {
      // Oldest first while assigning nth (fill #1, #2, ...), then reversed
      // for display - same order the demo generator produces.
      const chronological = events
        .filter((e) => e.payload.formId === f.id && e.contact_id != null)
        .slice()
        .sort((a, b) => a.occurred_at.localeCompare(b.occurred_at));

      const fillsPerContact = new Map<number, number>();
      const submissions: FormSubmission[] = chronological.map((e) => {
        const contactId = e.contact_id!;
        const nth = (fillsPerContact.get(contactId) ?? 0) + 1;
        fillsPerContact.set(contactId, nth);
        return {
          contactId,
          name: nameFor(contactId, contactsById),
          email: contactsById.get(contactId)?.email_jsonb?.[0]?.email ?? "",
          at: e.occurred_at,
          pageUrl: e.payload.pageUrl ?? "",
          channel: classifyChannel(e.payload.touch),
          nth,
        };
      });
      submissions.sort((a, b) => b.at.localeCompare(a.at));

      byForm.set(f.id, {
        submissions,
        uniqueContacts: fillsPerContact.size,
        last30: submissions.filter((s) => new Date(s.at).getTime() >= cutoff).length,
        lastAt: submissions[0]?.at,
        repeatFillers: [...fillsPerContact.entries()]
          .filter(([, fills]) => fills > 1)
          .map(([contactId, fills]) => ({
            contactId,
            name: nameFor(contactId, contactsById),
            fills,
          }))
          .sort((a, b) => b.fills - a.fills),
      });
    }
    return byForm;
  }, [forms, contacts, events]);
};
